import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { testApi } from "../../api/testApi";
import testNotFound from "../../assets/no-test-found.png";
import resultBg from "@/assets/result_bg.svg";
import { Info } from "lucide-react";
import CertificateModal from "@/components/certificates/CertificateModal";
import { toast } from "sonner";

export default function TestResult() {
  const navigate = useNavigate();
  const { courseId, attemptId } = useParams();
  const [courseName, setCourseName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [minPassPercentage, setMinPassPercentage] = useState(70);
  const [testId, setTestId] = useState(null);
  const [certificateOpen, setCertificateOpen] = useState(false);
  useEffect(() => {
    const fetchAttemptResult = async () => {
      if (!attemptId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch attempt results using the attemptId
        const response = await testApi.getAttemptResults(attemptId);
        const testResults = response.data;

        // Set result data
        setResult({
          score: testResults.correctAnswers || 0,
          total: testResults.totalQuestions || 0,
          passed: testResults.passed || false,
          percentage: testResults.percentage || 0,
          correctAnswers: testResults.correctAnswers || 0,
          wrongAnswers:
            (testResults.totalQuestions || 0) -
            (testResults.correctAnswers || 0),
        });
        setCourseName(testResults.course || "");
        setTestId(testResults.testId || null);
        setMinPassPercentage(testResults.minPassPercentage || 70);
      } catch (error) {
        console.error("Failed to fetch attempt result:", error);
        setError(error.message || "Failed to load test results.");
      } finally {
        setLoading(false);
      }
    };

    fetchAttemptResult();
  }, [attemptId]);

  const handleBackHome = () => {
    navigate(`/dashboard/courses/${courseId}`, {
      state: { courseName: courseName },
    });
  };

  const handleViewCertificate = () => {
    if (!courseId) {
      toast.error("Course not found for this certificate.");
      return;
    }
    setCertificateOpen(true);
  };

  const handleReAttempt = () => {
    // Navigate back to the assessment to start a new attempt
    navigate(`/dashboard/courses/${courseId}/test/${testId}`);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-[var(--secondary-font)]"></div>
        <p className="text-[var(--secondary-font)] font-bold">
          Loading results...
        </p>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center px-4 text-center">
        <img
          src={testNotFound}
          alt="Results Not Found"
          className="w-64 h-64 mb-6 opacity-90"
        />
        <div className="text-2xl font-bold text-gray-700 mb-2">
          Results Not Found
        </div>
        <div className="text-gray-500 text-lg mb-4">
          {error || "Could not load test results."}
        </div>
        <Button
          className="mt-2 px-8 py-2 rounded-full bg-[var(--secondary-font)] text-white hover:bg-[var(--primary-font)]"
          onClick={handleBackHome}
        >
          Back to Course
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex min-h-[100dvh] w-full items-center justify-center bg-gray-100 px-4 font-sans">
      <img
        src={resultBg}
        alt="Result Background"
        className="absolute z-1 inset-0 bottom-0 top-100 w-full h-full object-contains"
      />
      <div className="rounded-xl p-12 w-full max-w-md text-center z-100">
        <div className="mb-8">
          <div className="text-lg mb-4">You've scored</div>
          <div
            className={`text-8xl font-bold mb-4 ${result.passed ? "text-[#2B6A7F]" : "text-[#A98819]"}`}
          >
            {typeof result.percentage === "number" ? result.percentage.toFixed(2) : 0}%
          </div>
          <div className="text-xl font-semibold text-black mb-6">
            {courseName}
          </div>

          <div className="mb-6 font-sans">
            {result.passed ? (
              <Badge className="text-sm uppercase font-semibold px-6 py-2 rounded-full bg-green-100 text-[#47864D] border-green-200">
                CLEARED
              </Badge>
            ) : (
              <Badge className="text-sm uppercase font-semibold px-6 py-2 rounded-full bg-[#EAEAEA] text-[#A4A4A4] border-gray-300">
                NOT CLEARED
              </Badge>
            )}
          </div>

          <div className={`p-4 rounded-lg mb-8 bg-[#D9ECF8]`}>
            <div className="text-sm flex items-center gap-2 font-semibold text-[#2B6A7F] text-left">
              <Info className="w-6 h-6" />
              {result.passed
                ? `Well done — you've successfully passed with a score above ${minPassPercentage}%!`
                : `You need to score higher than ${minPassPercentage}% to pass this course.`}
            </div>
          </div>

          <div className="px-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Correct answers</span>
              <span className="font-semibold text-[#6ea6b9]">
                {result.correctAnswers}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Wrong answers</span>
              <span className="font-semibold text-[#6ea6b9]">
                {result.wrongAnswers}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {result.passed ? (
            <Button
              className="w-full px-8 py-3 rounded-full bg-[#5D93A5] text-white text-lg hover:bg-[var(--secondary-font)] transition-all"
              onClick={handleViewCertificate}
            >
              View certificate
            </Button>
          ) : (
            <Button
              className="w-full px-8 py-3 rounded-full bg-[#5D93A5] text-white text-lg hover:bg-[var(--secondary-font)] transition-all"
              onClick={handleReAttempt}
            >
              Re-attempt
            </Button>
          )}

          <Button
            variant="ghost"
            className="w-full px-8 py-2 border-2 border-[#0000001A] rounded-full bg-transparent text-black font-semibold hover:bg-gray-50 transition-all"
            onClick={handleBackHome}
          >
            Back home
          </Button>
        </div>
      </div>
      <CertificateModal
        courseId={courseId}
        open={certificateOpen}
        onOpenChange={setCertificateOpen}
      />
    </div>
  );
}
